/**
 * The memorial page model (M6 spec §5, §8; plan P32): the captains who died in the player's service, newest first
 * (the worker's `CaptainView.memorial` order). A quiet page: name, ship, place, day and cause, with the service
 * record (days served, runs, kills) in small type. Pure.
 */
import type { MemorialEntry } from '../../../strategic/types'
import { factionName } from '../../strategic/names'
import { panelCatalog, type PanelCatalog } from './catalog'
import { portraitRecipe, type PortraitRecipe } from './portrait'
import type { CaptainsConfig } from './captains'

export interface MemorialNames {
  /** A system id's name. */
  system(id: string): string
}

export interface MemorialRow {
  /** Unique per row (a captain id is unique; the index keeps a reused id apart). */
  key: string
  name: string
  portrait: PortraitRecipe
  /** "Lantern (Hauler (medium))". */
  ship: string
  place: string
  day: string
  cause: string
  service: string
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/** "Killed by Red Tally", or the unknown cause. */
export function causeText(killer: MemorialEntry['killer'], cfg: CaptainsConfig): string {
  return killer === null ? cfg.causeUnknown : cfg.causeKilledBy.replace('{faction}', factionName(killer))
}

export function memorialRows(entries: readonly MemorialEntry[], names: MemorialNames, cfg: CaptainsConfig, cat: PanelCatalog = panelCatalog()): MemorialRow[] {
  return entries.map(
    (e, i): MemorialRow => ({
      key: `${e.captainId}#${i}`,
      name: e.name,
      portrait: portraitRecipe(e.name, e.portrait, cfg.memorialTint),
      ship: `${e.shipName} (${cat.config.classLabels[e.shipClass]})`,
      place: names.system(e.systemId),
      day: `Day ${e.day}`,
      cause: causeText(e.killer, cfg),
      service: `Served ${plural(e.daysServed, 'day', 'days')} · ${plural(e.runs, 'run', 'runs')} · ${plural(e.kills, 'kill', 'kills')}`,
    }),
  )
}
